import { indicate_moving_vertices, moving_coordinates_visible } from '@features/indicate-vertices';
import type { Vertex } from '@phi/jcore';
import {
    move_vertices,
    start_move_vertices,
} from '@frontend/JCore/jcore';
import App from '@frontend/application-container';
import {
    get_dragged_manipulator_disk_rotation_angle,
    plane_ray_intersect,
    rotate_to_closest_global_coordinate_axis_step,
    round_scalar_to_grid,
} from '@frontend/geometry_and_math';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { proximate, roundToMultiple } from '@frontend/maths';
import {
    uid_scale_objects,
    uid_start_scale_objects,
} from '@frontend/ui_display';
import {
    uil_drag_edge_tangent_manipulator,
    uil_drag_vertex_normal_manipulator,
    uil_get_vertices_of_edge,
    uil_rotate_edge_list_around_axis,
    uil_translate_edge_list,
} from '@frontend/ui_logic';
import {
    display_tangencies,
    fix_edge_tangent_at_vertex,
    hide_tangencies,
    unfix_edge_tangent_at_vertex,
} from '@frontend/uid_continuity';
import { identify_snapping_targets } from '@frontend/utility';
import {
    approximately_coaxial,
    point_point_distance,
} from '@frontend/vector3';
import { Vector3 } from 'three';

export function rigid_move_manipulator_cube(modifier: boolean) {
    // Fully highlight the manipulator object
    // @ts-ignore
    uid_rigid_move_manipulator!.cube.material.color.setRGB(
        App.state.colours.selected.r / 256,
        App.state.colours.selected.g / 256,
        App.state.colours.selected.b / 256,
    );
    // Send translation to core to move objects.
    if (!modifier)
        // Two different types of movement depending on whether this is an edge list or just one vertex
        if (!uid_rigid_move_manipulator!.rigid_move_face_object_id && uid_rigid_move_manipulator!.rigid_move_edge_object_ids.length == 0) {
            // Moving just a vertex
            App.state.dragging_type = 'vertex';
            if (uid_state.in_first_drag_step) {
                uid_in_dragging_mode = true;

                if (moving_coordinates_visible.value)
                    indicate_moving_vertices([uid_rigid_move_manipulator!.indicated_center_object_id as Vertex]);
                start_move_vertices([uid_rigid_move_manipulator!.indicated_center_object_id as Vertex]);
            }
            move_vertices([uid_rigid_move_manipulator!.indicated_center_object_id as Vertex], uid_manip_current_raycaster_origin, uid_manip_current_raycaster_direction);
            uid_state.in_first_drag_step = false;
        } else {
            // Moving an edge list
            uil_translate_edge_list(
                uid_rigid_move_manipulator!.rigid_move_edge_object_ids,
                uid_manip_previous_raycaster_origin,
                uid_manip_previous_raycaster_direction,
                uid_manip_current_raycaster_origin,
                uid_manip_current_raycaster_direction
            );
            uid_state.in_first_drag_step = false;
        }

    // Also translate manipulator.
    uid_rigid_move_manipulator!.translate_to_ray(
        uid_manip_previous_raycaster_origin,
        uid_manip_previous_raycaster_direction,
        uid_manip_current_raycaster_origin,
        uid_manip_current_raycaster_direction,
    );
}

export function rigid_move_manipulator_ring(modifier: boolean) {
    let local_plane_normal: Vector3;

    if (uid_selected_manipulator_type.includes('rigid_move_manip_ringx')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.ringx.material.color.setRGB(
            App.state.colours.selected.r / 256,
            App.state.colours.selected.g / 256,
            App.state.colours.selected.b / 256,
        );
        local_plane_normal = uid_rigid_move_manipulator!.local_x_direction;
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_ringy')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.ringy.material.color.setRGB(
            App.state.colours.selected.r / 256,
            App.state.colours.selected.g / 256,
            App.state.colours.selected.b / 256,
        );
        local_plane_normal = uid_rigid_move_manipulator!.local_y_direction;
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_ringz')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.ringz.material.color.setRGB(
            App.state.colours.selected.r / 256,
            App.state.colours.selected.g / 256,
            App.state.colours.selected.b / 256,
        );
        local_plane_normal = uid_rigid_move_manipulator!.local_z_direction;
    }

    // Find a normal to the plane that matches the raycasting direction
    const normal_along_view_direction = new Vector3();
    normal_along_view_direction.copy(local_plane_normal!);
    if (normal_along_view_direction.dot(uid_manip_current_raycaster_direction) < 0)
        normal_along_view_direction.set(-1 * local_plane_normal!.x, -1 * local_plane_normal!.y, -1 * local_plane_normal!.z);

    const angle = get_dragged_manipulator_disk_rotation_angle(
        uid_rigid_move_manipulator!.center,
        normal_along_view_direction,
        uid_rigid_move_manipulator!.ring_radius,
        uid_manip_previous_raycaster_origin,
        uid_manip_previous_raycaster_direction,
        uid_manip_current_raycaster_origin,
        uid_manip_current_raycaster_direction
    );

    let adjusted_angle = angle;
    if (angle > Math.PI)
        adjusted_angle -= 2 * Math.PI;

    if (adjusted_angle < 0) {
        // Flip angle value and axis direction
        adjusted_angle *= -1;
        normal_along_view_direction.multiplyScalar(-1);
    }

    // If the grid is visible, snap so that the normal ends up at steps of
    if (App.state.grid_visible) {
        const angle_step = 30 * Math.PI / 180;
        if (uid_state.in_first_drag_step) {
            // Must rotate the selected objects and the manipulator so that they become coincident with the closest step of the global
            // xyz coordinate axes.
            rotate_to_closest_global_coordinate_axis_step(uid_rigid_move_manipulator!, angle_step);
        }
        adjusted_angle = roundToMultiple(adjusted_angle, angle_step);
    }

    if (adjusted_angle != 0 || uid_state.in_first_drag_step) {
        if (!modifier) {
            if (
                App.state.editing_edge_tangent_mode
                && uid_rigid_move_manipulator!.is_for_edge_tangent
                && uid_rigid_move_manipulator!.rigid_move_edge_object_ids.length == 1
                && !uid_rigid_move_manipulator!.rigid_move_face_object_id
                && uid_rigid_move_manipulator!.rigid_move_vertex_object_ids.length == 1
                && uil_get_vertices_of_edge(uid_rigid_move_manipulator!.rigid_move_edge_object_ids[0]!)
                    .includes(uid_rigid_move_manipulator!.rigid_move_vertex_object_ids[0]!)
            ) {
                // Unfix the edge tangent.
                unfix_edge_tangent_at_vertex(
                    uid_rigid_move_manipulator!.rigid_move_edge_object_ids[0]!,
                    [uid_rigid_move_manipulator!.rigid_move_vertex_object_ids[0]!],
                    false,
                );

                uil_drag_edge_tangent_manipulator(
                    uid_rigid_move_manipulator!.rigid_move_edge_object_ids[0]!,
                    uid_rigid_move_manipulator!.rigid_move_vertex_object_ids[0]!,
                    normal_along_view_direction,
                    adjusted_angle
                );

                // Fix the edge tangent.
                fix_edge_tangent_at_vertex(
                    uid_rigid_move_manipulator!.rigid_move_edge_object_ids[0]!,
                    [uid_rigid_move_manipulator!.rigid_move_vertex_object_ids[0]!]
                );

                // Re-display tangencies
                hide_tangencies(true);
                display_tangencies(false, true);
            } else if (
                uid_rigid_move_manipulator!.rigid_move_edge_object_ids.length == 0
                && !uid_rigid_move_manipulator!.rigid_move_face_object_id
                && uid_rigid_move_manipulator!.rigid_move_vertex_object_ids.length == 1
            )
                uil_drag_vertex_normal_manipulator(
                    uid_rigid_move_manipulator!.rigid_move_vertex_object_ids[0]!,
                    normal_along_view_direction,
                    adjusted_angle
                );
            else
                uil_rotate_edge_list_around_axis(
                    uid_rigid_move_manipulator!.rigid_move_edge_object_ids,
                    uid_rigid_move_manipulator!.center,
                    normal_along_view_direction,
                    adjusted_angle,
                );
            uid_state.in_first_drag_step = false;
        } else {
            const adjusted_angle = angle > Math.PI
                ? angle * -1
                : angle;
            const adjusted_axis = new Vector3().copy(normal_along_view_direction);
            uid_rigid_move_manipulator!.rotate_around_axis(uid_rigid_move_manipulator!.center, adjusted_axis, adjusted_angle);
        }
    } else {
        // Roll back to previous raycaster, as this step is skipped.
        uid_manip_current_raycaster_origin.copy(uid_manip_previous_raycaster_origin);
        uid_manip_current_raycaster_direction.copy(uid_manip_previous_raycaster_direction);
    }
}

export function rigid_move_manipulator_rod(modifier: boolean) {
    const local_move_direction = new Vector3();
    if (uid_selected_manipulator_type.includes('rigid_move_manip_vectorx_rod')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.axisx_rod.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_move_direction.copy(uid_rigid_move_manipulator!.local_x_direction);
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_vectory_rod')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.axisy_rod.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_move_direction.copy(uid_rigid_move_manipulator!.local_y_direction);
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_vectorz_rod')) {
        // @ts-ignore
        uid_rigid_move_manipulator!.axisz_rod.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_move_direction.copy(uid_rigid_move_manipulator!.local_z_direction);
    } else if (uid_selected_manipulator_type == 'rigid_move_manip_fence_tube') {
        // @ts-ignore
        uid_rigid_move_manipulator!.fence_tube.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_move_direction.copy(uid_rigid_move_manipulator!.local_x_direction);
    }

    // Limit movement of mouse along manipulator axis.
    // 1. Find projection of manipulator axis onto view plane:
    const axis_proj = new Vector3();
    const d = local_move_direction.dot(uid_manip_previous_raycaster_direction);
    axis_proj.copy(local_move_direction);
    axis_proj.addScaledVector(uid_manip_previous_raycaster_direction, -1 * d);

    // 2. Create a plane with base the raycaster previous origin and normal the projected axis, and intersect it with the manipulator axis.
    const p_previous = plane_ray_intersect(uid_manip_previous_raycaster_origin, axis_proj, uid_rigid_move_manipulator!.center, local_move_direction, 1);
    const p_current = plane_ray_intersect(uid_manip_current_raycaster_origin, axis_proj, uid_rigid_move_manipulator!.center, local_move_direction, 1);

    if (p_current == null || p_previous == null || isNaN(p_current.x) || isNaN(p_current.y) || isNaN(p_current.z) || isNaN(p_previous.x) || isNaN(p_previous.y) || isNaN(p_previous.z)) {
        // User somehow dragged an axis along a direction that is geometrically impossible, abort drag step.
        return;
    }
    const p_previous_adjusted = new Vector3();
    p_previous_adjusted.copy(p_previous);
    p_previous_adjusted.sub(uid_snap_delta);

    //console_log('P previous: ', p_previous.y);
    //console_log('Snap delta: ', uid_snap_delta.y);
    //console_log('P previous adjusted: ', p_previous_adjusted.y);
    //console_log('P current: ', p_current.y);

    // 3. If we are displaying a grid and the axis direction is X, Y or Z and parallel to the grid plane,
    //    snap the center of the manipulator on the grid along the axis direction only
    const local_epsilon = 1e-6;

    let snapped_center: Vector3 | null = null;
    if (
        App.state.grid_visible
        && snap_to_grid
        && viewing_down_orthogonal_plane
        && (
            (
                proximate(Math.abs(local_move_direction.x), 1, local_epsilon)
                && proximate(Math.abs(local_move_direction.y), 0, local_epsilon)
                && proximate(Math.abs(local_move_direction.z), 0, local_epsilon)
                && (uid_grid_normal == 'y' || uid_grid_normal == 'z')
            ) || (
                proximate(Math.abs(local_move_direction.x), 0, local_epsilon)
                && proximate(Math.abs(local_move_direction.y), 1, local_epsilon)
                && proximate(Math.abs(local_move_direction.z), 0, local_epsilon)
                && (uid_grid_normal == 'x' || uid_grid_normal == 'z')
            ) || (
                proximate(Math.abs(local_move_direction.x), 0, local_epsilon)
                && proximate(Math.abs(local_move_direction.y), 0, local_epsilon)
                && proximate(Math.abs(local_move_direction.z), 1, local_epsilon)
                && (uid_grid_normal == 'x' || uid_grid_normal == 'y')
            )
        )
    ) {
        const current_center = uid_rigid_move_manipulator!.center;
        const delta = new Vector3();
        delta.copy(p_current);
        delta.sub(p_previous_adjusted);
        const test_center = new Vector3();
        test_center.copy(current_center);
        test_center.add(delta);

        //console_log('Current center:', current_center.y);
        //console_log('Delta1:', delta.y);
        //console_log('Test center:', test_center.y);

        if (proximate(Math.abs(local_move_direction.x), 1, local_epsilon)) {
            // Moving along x
            snapped_center = new Vector3(round_scalar_to_grid(test_center.x, uid_grid_origin.x, uid_grid_step), current_center.y, current_center.z);
        } else if (proximate(Math.abs(local_move_direction.y), 1, local_epsilon)) {
            // Moving along y
            snapped_center = new Vector3(current_center.x, round_scalar_to_grid(test_center.y, uid_grid_origin.y, uid_grid_step), current_center.z);
        } else if (proximate(Math.abs(local_move_direction.z), 1, local_epsilon)) {
            // Moving along z
            snapped_center = new Vector3(current_center.x, current_center.y, round_scalar_to_grid(test_center.z, uid_grid_origin.z, uid_grid_step));
        }

        //console_log('Snapped center:', snapped_center.y);

        // Add difference vector betwee current and snapped manipulator center to p_current
        uid_snap_delta.copy(test_center);
        uid_snap_delta.sub(snapped_center!);
        //console_log('Snap Delta:', uid_snap_delta.y);
        p_current.sub(uid_snap_delta);
        //console_log('-----> New p_current:', p_current.y);
    } else {
        uid_snap_delta = new Vector3(0, 0, 0);
    }

    // Send translation to core to move objects.
    if (!modifier) {
        // Two different types of movement depending on whether this is an edge list or just one vertex
        if (!uid_rigid_move_manipulator!.rigid_move_face_object_id && uid_rigid_move_manipulator!.rigid_move_edge_object_ids.length == 0) {
            // Moving just a vertex
            App.state.dragging_type = 'vertex';
            if (uid_state.in_first_drag_step) {
                uid_in_dragging_mode = true;
                // Collect vertices to drag
                uid_additional_drag_vertices = [];
                uid_additional_drag_vertices.push(uid_rigid_move_manipulator!.indicated_center_object_id as Vertex);
                uid_selected_vertices.keys().forEach(function (vid){
                    if (vid != uid_rigid_move_manipulator!.indicated_center_object_id) {
                        uid_additional_drag_vertices.push(vid);
                    }
                });

                if (moving_coordinates_visible.value)
                    indicate_moving_vertices(uid_additional_drag_vertices);

                start_move_vertices(uid_additional_drag_vertices);
                uid_rigid_move_manipulator!.vertex_drag_offset.copy(uid_vertices.getItem(uid_rigid_move_manipulator!.indicated_center_object_id as Vertex)!.object3d!.position);
                uid_rigid_move_manipulator!.vertex_drag_offset.sub(p_previous);
                uid_state.in_first_drag_step = false;
            }
            // Calculate the location and direction of a drag mechanism based on the vertex we are moving and where the mouse is pointing.
            // Find a normal to the move direction by crossing it with a random vector
            const n = new Vector3();
            n.x = 0.316227766;
            n.y = 0.948683298;
            n.z = 0.572665981;
            n.cross(local_move_direction).normalize();
            const p_current_adjusted = new Vector3();
            p_current_adjusted.copy(p_current);
            p_current_adjusted.add(uid_rigid_move_manipulator!.vertex_drag_offset);
            //console_log('Vertex drag offset:', uid_rigid_move_manipulator.vertex_drag_offset);
            if (!snapped_center)
                move_vertices(uid_additional_drag_vertices, p_current_adjusted, n);
            else
                move_vertices(uid_additional_drag_vertices, snapped_center, n);
        } else {
            if (uid_rigid_move_manipulator!.is_for_fence && uid_selected_manipulator_type == 'rigid_move_manip_fence_tube') {
                // Do nothing for now XXX
                // console_log('-----> New p_current:', p_current.y);
            } else {
                // Moving an edge list
                if (p_previous_adjusted.distanceTo(p_current) > 0) {
                    uil_translate_edge_list(
                        uid_rigid_move_manipulator!.rigid_move_edge_object_ids,
                        p_previous_adjusted, uid_manip_previous_raycaster_direction,
                        p_current, uid_manip_current_raycaster_direction
                    );
                    uid_state.in_first_drag_step = false;
                }
            }
        }

        // Snap if necessary
        if (snapping_is_selected)
            identify_snapping_targets('rigid_move', uid_rigid_move_manipulator!, true, false);
    }

    // Also translate manipulator.
    if (uid_rigid_move_manipulator!.is_for_fence && uid_selected_manipulator_type == 'rigid_move_manip_fence_tube')
        uid_rigid_move_manipulator!.translate_along_edge(p_previous_adjusted, uid_manip_previous_raycaster_direction, p_current, uid_manip_current_raycaster_direction);
    else
        uid_rigid_move_manipulator!.translate_to_ray(p_previous_adjusted, uid_manip_previous_raycaster_direction, p_current, uid_manip_current_raycaster_direction);
}

export function rigid_move_manipulator_plane(modifier: boolean) {
    const local_plane_normal = new Vector3();
    if (uid_selected_manipulator_type == 'rigid_move_manip_planexy') {
        // @ts-ignore
        uid_rigid_move_manipulator!.planexy.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_plane_normal.copy(uid_rigid_move_manipulator!.local_z_direction);
    } else if (uid_selected_manipulator_type == 'rigid_move_manip_planeyz') {
        // @ts-ignore
        uid_rigid_move_manipulator!.planeyz.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_plane_normal.copy(uid_rigid_move_manipulator!.local_x_direction);
    } else if (uid_selected_manipulator_type == 'rigid_move_manip_planexz') {
        // @ts-ignore
        uid_rigid_move_manipulator!.planexz.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
        local_plane_normal.copy(uid_rigid_move_manipulator!.local_y_direction);
    }

    // Limit movement of mouse within plane.
    // Intersect previous and current raycaster direction with plane, and find difference
    let local_epsilon = 1e-6;
    const p_previous = plane_ray_intersect(
        uid_rigid_move_manipulator!.center,
        local_plane_normal,
        uid_manip_previous_raycaster_origin,
        uid_manip_previous_raycaster_direction,
        local_epsilon,
    );
    const p_current = plane_ray_intersect(
        uid_rigid_move_manipulator!.center,
        local_plane_normal,
        uid_manip_current_raycaster_origin,
        uid_manip_current_raycaster_direction,
        local_epsilon,
    );

    const p_previous_adjusted = new Vector3();
    p_previous_adjusted.copy(p_previous);
    p_previous_adjusted.sub(uid_snap_delta);

    //console_log('P previous: ', p_previous.y);
    //console_log('Snap delta: ', uid_snap_delta.y);
    //console_log('P previous adjusted: ', p_previous_adjusted.y);
    //console_log('P current: ', p_current.y);

    // 3. If we are displaying a grid and the axis direction is X, Y or Z and parallel to the grid plane,
    //    snap the center of the manipulator on the grid
    local_epsilon = 1e-6;

    let snapped_center: Vector3 | null = null;

    if (
        App.state.grid_visible
        && snap_to_grid
        && viewing_down_orthogonal_plane
        && (
            (proximate(Math.abs(local_plane_normal.x), 1, local_epsilon))
            || (proximate(Math.abs(local_plane_normal.y), 1, local_epsilon))
            || (proximate(Math.abs(local_plane_normal.z), 1, local_epsilon))
        )
    ) {
        const current_center = uid_rigid_move_manipulator!.center;

        const delta = new Vector3();
        delta.copy(p_current);
        delta.sub(p_previous_adjusted);
        const test_center = new Vector3();
        test_center.copy(current_center);
        test_center.add(delta);

        //console_log('Current center:', current_center.y);
        //console_log('Delta1:', delta.y);
        //console_log('Test center:', test_center.y);

        if (uid_grid_normal == 'x') {
            snapped_center = new Vector3(
                current_center.x,
                round_scalar_to_grid(test_center.y, uid_grid_origin.y, uid_grid_step),
                round_scalar_to_grid(test_center.z, uid_grid_origin.z, uid_grid_step)
            );
        } else if (uid_grid_normal == 'y') {
            snapped_center = new Vector3(
                round_scalar_to_grid(test_center.x, uid_grid_origin.x, uid_grid_step),
                current_center.y,
                round_scalar_to_grid(test_center.z, uid_grid_origin.z, uid_grid_step)
            );
        } else if (uid_grid_normal == 'z') {
            snapped_center = new Vector3(
                round_scalar_to_grid(test_center.x, uid_grid_origin.x, uid_grid_step),
                round_scalar_to_grid(test_center.y, uid_grid_origin.y, uid_grid_step),
                current_center.z,
            );
        }

        //console_log('Snapped center:', snapped_center.y);

        // Add difference vector betwee current and snapped manipulator center to p_current
        uid_snap_delta.copy(test_center);
        uid_snap_delta.sub(snapped_center!);
        //console_log('Snap Delta:', uid_snap_delta.y);
        p_current.sub(uid_snap_delta);
        //console_log('-----> New p_current:', p_current.y);
    } else {
        uid_snap_delta = new Vector3(0, 0, 0);
    }

    if (!modifier) {
        // Two different types of movement depending on whether this is an edge list or just one vertex
        if (!uid_rigid_move_manipulator!.rigid_move_face_object_id && uid_rigid_move_manipulator!.rigid_move_edge_object_ids.length == 0) {
            // Moving just a vertex
            App.state.dragging_type = 'vertex';
            if (uid_state.in_first_drag_step) {
                uid_in_dragging_mode = true;
                // Collect vertices to drag
                uid_additional_drag_vertices = [];
                uid_additional_drag_vertices.push(uid_rigid_move_manipulator!.indicated_center_object_id as Vertex);
                uid_selected_vertices.keys().forEach(function (vid){
                    if (vid != uid_rigid_move_manipulator!.indicated_center_object_id as Vertex) {
                        uid_additional_drag_vertices.push(vid);
                    }
                });

                if (moving_coordinates_visible.value)
                    indicate_moving_vertices(uid_additional_drag_vertices);

                start_move_vertices(uid_additional_drag_vertices);
                uid_state.in_first_drag_step = false;
                uid_rigid_move_manipulator!.vertex_drag_offset.copy(uid_vertices.getItem(uid_rigid_move_manipulator!.indicated_center_object_id as Vertex)!.object3d!.position);
                uid_rigid_move_manipulator!.vertex_drag_offset.sub(p_previous);
            }
            const p_current_adjusted = new Vector3();
            p_current_adjusted.copy(p_current);
            p_current_adjusted.add(uid_rigid_move_manipulator!.vertex_drag_offset);
            //console_log('Vertex drag offset:', uid_rigid_move_manipulator.vertex_drag_offset);
            if (!snapped_center!)
                move_vertices(uid_additional_drag_vertices, p_current_adjusted, local_plane_normal);
            else
                move_vertices(uid_additional_drag_vertices, snapped_center, local_plane_normal);
        } else {
            // Moving an edge list
            if (p_previous_adjusted.distanceTo(p_current) > 0) {
                uil_translate_edge_list(
                    uid_rigid_move_manipulator!.rigid_move_edge_object_ids,
                    p_previous_adjusted,
                    uid_manip_previous_raycaster_direction,
                    p_current,
                    uid_manip_current_raycaster_direction,
                );
                uid_state.in_first_drag_step = false;
            }
        }

        // Snap if necessary
        if (snapping_is_selected)
            identify_snapping_targets('rigid_move', uid_rigid_move_manipulator!, true, false);
    }

    // Also translate manipulator.
    uid_rigid_move_manipulator!.translate_to_ray(p_previous_adjusted, uid_manip_previous_raycaster_direction, p_current, uid_manip_current_raycaster_direction);
}

export function rigid_move_manipulator_tip(modifier: boolean) {
    if (App.state.dragging_type != 'scale_edge_list') {
        uid_start_scale_objects(uid_selected_vertices.length == 1);
    }

    // @ts-ignore
    uid_rigid_move_manipulator!.axisx_tip.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
    // @ts-ignore
    uid_rigid_move_manipulator!.axisy_tip.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);
    // @ts-ignore
    uid_rigid_move_manipulator!.axisz_tip.material.color.setRGB(App.state.colours.selected.r / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256);

    // The initial distance between the center and the vector tip is this.axis_vector_size
    // Find the current distance
    let local_move_direction: Vector3;
    if (uid_selected_manipulator_type.includes('rigid_move_manip_vectorx_tip')) {
        local_move_direction = uid_rigid_move_manipulator!.local_x_direction;
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_vectory_tip')) {
        local_move_direction = uid_rigid_move_manipulator!.local_y_direction;
    } else if (uid_selected_manipulator_type.includes('rigid_move_manip_vectorz_tip')) {
        local_move_direction = uid_rigid_move_manipulator!.local_z_direction;
    }

    // 1. Find projection of manipulator axis onto view plane:
    const axis_proj = new Vector3();
    const d = local_move_direction!.dot(uid_manip_current_raycaster_direction);
    axis_proj.copy(local_move_direction!);
    axis_proj.addScaledVector(uid_manip_current_raycaster_direction, -1 * d);

    // 2. Create a plane with base the raycaster previous origin and normal the projected axis, and intersect it with the manipulator axis.
    const p_previous = plane_ray_intersect(uid_manip_previous_raycaster_origin, axis_proj, uid_rigid_move_manipulator!.center, local_move_direction!, 1);
    const p_current = plane_ray_intersect(uid_manip_current_raycaster_origin, axis_proj, uid_rigid_move_manipulator!.center, local_move_direction!, 1);
    const previous_dist = point_point_distance(p_previous, uid_rigid_move_manipulator!.center);
    const current_dist = point_point_distance(p_current, uid_rigid_move_manipulator!.center);
    const factor = current_dist/previous_dist;

    let scale_axis = null;
    const local_epsilon = 1e-6;

    if (!modifier) {
        // We should scale along the axis direction. But if that's not aligned with the x, y, or z we cannot.
        const vx = new Vector3(1, 0, 0);
        const vy = new Vector3(0, 1, 0);
        const vz = new Vector3(0, 0, 1);
        // display_message_to_user(LOG.INFO, 'v: ' + local_move_direction.x + ', ' + local_move_direction.y + ', ' + local_move_direction.z);
        if (
            approximately_coaxial(local_move_direction!, vx, local_epsilon)
            || approximately_coaxial(local_move_direction!, vy, local_epsilon)
            || approximately_coaxial(local_move_direction!, vz, local_epsilon)
        )
            scale_axis = new Vector3(local_move_direction!.x, local_move_direction!.y, local_move_direction!.z);
        else
            display_message_to_user(LOG.WARN, 'The scaling is proportional, because the scale direction is not aligned with the X, Y or Z axis. Temporary limitation');
    }
    uid_scale_objects(factor, scale_axis!, uid_selected_vertices.length == 1);
}
